import React, { useState, useEffect } from 'react';
import { Badge, Row, Col, Table, Button } from 'react-bootstrap';
import Loader from '../../components/widgets/Loader';
import axios from '../../services/axios';

const ROLE_LABEL = {
    chef_etablissement: "Chef d'établissement",
    referent_projet: 'Référent projet',
    prof_documentaliste: 'Prof documentaliste',
    psychologue: 'Psychologue',
    enseignant: 'Enseignant',
    autre: 'Autre',
};

const NIVEAU_LABEL = {
    '6e': '6e',
    '5e': '5e',
    '4e': '4e',
    '3e': '3e',
    '3e_prepa_metiers': '3e prépa métiers',
    '2nde': '2nde',
    '1ere': '1re',
    'terminale': 'Terminale',
};

const MODALITE_LABEL = {
    classe_entiere: 'Classe entière',
    demi_groupe: 'Demi-groupe',
    en_classe: 'En classe',
};

const PERIODE_LABEL = {
    S1: '1er semestre',
    S2: '2ème semestre',
};

const isBfcData = (donnees) =>
    donnees !== null && typeof donnees === 'object' && 'equipe' in donnees;

const DiffField = ({ original, corrected, render = v => v }) => {
    const hasCorrection = corrected !== undefined && corrected !== null && corrected !== original;
    if (!hasCorrection) return <>{render(original) ?? '—'}</>;
    return (
        <>
            <s className="text-muted me-2">{render(original) ?? '—'}</s>
            <strong>{render(corrected)}</strong>
        </>
    );
};

const OperationInscriptionModalContent = ({ detailUrl, currentIndex, total, onNavigate, onInscriptionLoaded, availableTagOperations }) => {
    const [loading, setLoading] = useState(true);
    const [inscription, setInscription] = useState(null);

    useEffect(() => {
        if (!detailUrl) return;
        axios.get(detailUrl)
            .then(data => {
                setInscription(data);
                onInscriptionLoaded?.(data);
            })
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onInscriptionLoaded is a write-once callback, re-fetching on change is not desired
    }, [detailUrl]);

    if (loading) return <Loader />;
    if (!inscription) return null;

    const { donneesOperation: donnees } = inscription;
    const bfc = isBfcData(donnees);
    const op = inscription.operatorData?.donneesOperation;

    return (
        <div>
            {onNavigate && total > 1 && (
                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                    <Button
                        variant="link"
                        onClick={() => onNavigate(currentIndex - 1)}
                        disabled={currentIndex === 0}
                    >
                        <i className="fas fa-arrow-left me-1" />Précédente
                    </Button>
                    <span className="text-muted">{currentIndex + 1}/{total}</span>
                    <Button
                        variant="link"
                        onClick={() => onNavigate(currentIndex + 1)}
                        disabled={currentIndex === total - 1}
                    >
                        Inscription suivante<i className="fas fa-arrow-right ms-1" />
                    </Button>
                </div>
            )}

            <Row className="mb-3">
                <Col>
                    {inscription.uai && <span className="me-2 text-muted small">{inscription.uai}</span>}
                    <span className="text-muted small">{inscription.etablissementType}</span>
                </Col>
            </Row>

            <section className="mb-4">
                <h5>Établissement</h5>
                <dl className="row mb-0">
                    <dt className="col-sm-3">Adresse</dt>
                    <dd className="col-sm-9">{inscription.etablissementAdresse || '—'}</dd>
                    <dt className="col-sm-3">Code postal / Ville</dt>
                    <dd className="col-sm-9">{inscription.etablissementCp} {inscription.etablissementVille}</dd>
                </dl>
            </section>

            {bfc ? (
                <>
                    {donnees.equipe?.length > 0 && (
                        <section className="mb-4">
                            <h5>Équipe</h5>
                            <Table size="sm" bordered responsive>
                                <thead>
                                    <tr>
                                        <th>Civilité</th>
                                        <th>Prénom</th>
                                        <th>Nom</th>
                                        <th>Email</th>
                                        <th>Tél. mobile</th>
                                        <th>Tél. fixe</th>
                                        <th>Rôles</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {donnees.equipe.map((membre) => {
                                        const opMembre = op?.equipe?.find(m => m.id === membre.id);
                                        return (
                                            <tr key={membre.id}>
                                                <td><DiffField original={membre.civilite} corrected={opMembre?.civilite} /></td>
                                                <td><DiffField original={membre.prenom} corrected={opMembre?.prenom} /></td>
                                                <td><DiffField original={membre.nom} corrected={opMembre?.nom} /></td>
                                                <td><DiffField original={membre.email} corrected={opMembre?.email} /></td>
                                                <td><DiffField original={membre.telephone_mobile || null} corrected={opMembre?.telephone_mobile} /></td>
                                                <td><DiffField original={membre.telephone_fixe || null} corrected={opMembre?.telephone_fixe} /></td>
                                                <td>
                                                    {membre.roles?.map(role => (
                                                        <Badge key={role} bg="secondary" className="me-1">
                                                            {role === 'autre' && membre.role_autre_precis
                                                                ? `Autre : ${membre.role_autre_precis}`
                                                                : (ROLE_LABEL[role] ?? role)}
                                                        </Badge>
                                                    ))}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </section>
                    )}

                    <section className="mb-4">
                        <h5>Projet pédagogique</h5>
                        <dl className="row mb-0">
                            <dt className="col-sm-4">Nombre d'élèves</dt>
                            <dd className="col-sm-8"><DiffField original={donnees.nb_eleves} corrected={op?.nb_eleves} /></dd>
                            <dt className="col-sm-4">Niveaux</dt>
                            <dd className="col-sm-8">
                                {donnees.niveaux?.map(n => (
                                    <Badge key={n} bg="light" text="dark" className="me-1 border">
                                        {NIVEAU_LABEL[n] ?? n}
                                    </Badge>
                                ))}
                            </dd>
                            <dt className="col-sm-4">Modalités</dt>
                            <dd className="col-sm-8">
                                {donnees.modalites?.map(m => (
                                    <Badge key={m} bg="light" text="dark" className="me-1 border">
                                        {MODALITE_LABEL[m] ?? m}
                                    </Badge>
                                ))}
                            </dd>
                            {(donnees.scenarios_pedagogiques || op?.scenarios_pedagogiques) && (
                                <>
                                    <dt className="col-sm-4">Scénarios pédagogiques</dt>
                                    <dd className="col-sm-8" style={{ whiteSpace: 'pre-wrap' }}><DiffField original={donnees.scenarios_pedagogiques} corrected={op?.scenarios_pedagogiques} /></dd>
                                </>
                            )}
                        </dl>
                    </section>

                    <section className="mb-4">
                        <h5>Modalités du prêt</h5>
                        <dl className="row mb-0">
                            {(donnees.periode_pret_modalite || op?.periode_pret_modalite) && (
                                <>
                                    <dt className="col-sm-4">Période souhaitée</dt>
                                    <dd className="col-sm-8">
                                        <DiffField
                                            original={donnees.periode_pret_modalite}
                                            corrected={op?.periode_pret_modalite}
                                            render={v => PERIODE_LABEL[v] ?? v}
                                        />
                                    </dd>
                                </>
                            )}
                            {(donnees.webinaire_lancement || op?.webinaire_lancement) && (
                                <>
                                    <dt className="col-sm-4">Webinaire lancement</dt>
                                    <dd className="col-sm-8"><DiffField original={donnees.webinaire_lancement} corrected={op?.webinaire_lancement} /></dd>
                                </>
                            )}
                            {(donnees.webinaire_pedagogique || op?.webinaire_pedagogique) && (
                                <>
                                    <dt className="col-sm-4">Webinaire pédagogique</dt>
                                    <dd className="col-sm-8"><DiffField original={donnees.webinaire_pedagogique} corrected={op?.webinaire_pedagogique} /></dd>
                                </>
                            )}
                            {(donnees.message || op?.message) && (
                                <>
                                    <dt className="col-sm-4">Message</dt>
                                    <dd className="col-sm-8" style={{ whiteSpace: 'pre-wrap' }}><DiffField original={donnees.message} corrected={op?.message} /></dd>
                                </>
                            )}
                        </dl>
                    </section>
                </>
            ) : donnees && (
                <section className="mb-4">
                    <h5>Données de l'opération</h5>
                    <dl className="row mb-0">
                        {Object.entries(donnees).map(([key, value]) => (
                            <React.Fragment key={key}>
                                <dt className="col-sm-4">{key}</dt>
                                <dd className="col-sm-8">
                                    {typeof value === 'object'
                                        ? JSON.stringify(value)
                                        : String(value ?? '—')}
                                </dd>
                            </React.Fragment>
                        ))}
                    </dl>
                </section>
            )}

            <section className="mb-4">
                <h5>Historique</h5>
                <dl className="row mb-0">
                    <dt className="col-sm-4">Soumis le</dt>
                    <dd className="col-sm-8">
                        {inscription.createdAt
                            ? new Date(inscription.createdAt).toLocaleString('fr-FR')
                            : '—'}
                    </dd>
                    {inscription.donneesValidatedAt && (
                        <>
                            <dt className="col-sm-4">Données validées le</dt>
                            <dd className="col-sm-8">
                                {new Date(inscription.donneesValidatedAt).toLocaleString('fr-FR')}
                                {inscription.donneesValidatedBy && ` par ${inscription.donneesValidatedBy}`}
                            </dd>
                        </>
                    )}
                    {inscription.injectedAt && (
                        <>
                            <dt className="col-sm-4">Injecté le</dt>
                            <dd className="col-sm-8">
                                {new Date(inscription.injectedAt).toLocaleString('fr-FR')}
                            </dd>
                        </>
                    )}
                    {inscription.statut === 'rejected' && inscription.rejectedReason && (
                        <>
                            <dt className="col-sm-4">Motif de rejet</dt>
                            <dd className="col-sm-8">{inscription.rejectedReason}</dd>
                        </>
                    )}
                </dl>
            </section>
            {availableTagOperations?.length > 0 && (
                <dl className="row mb-0 border-top pt-3">
                    <dt className="col-sm-3">Tag opération</dt>
                    <dd className="col-sm-9">
                        {inscription.tagOperation
                            ? (() => {
                                const tag = availableTagOperations.find(t => t.uniqueId === inscription.tagOperation);
                                return tag ? <Badge bg="secondary">{tag.name}</Badge> : '—';
                            })()
                            : '—'}
                    </dd>
                </dl>
            )}
            <dl className="row mb-0 border-top pt-3">
                <dt className="col-sm-3">Commentaire</dt>
                <dd className="col-sm-9">
                    {inscription.comment
                        ? <em style={{ whiteSpace: 'pre-wrap' }}>{inscription.comment}</em>
                        : '—'}
                </dd>
            </dl>

        </div>
    );
};

export default OperationInscriptionModalContent;
